/**
 * PostingDocument (전표) streamType registry — Voucher Stream A SSOT
 *
 * @see .cursor/plans/_overview/VOUCHER_STREAMS_OVERVIEW.md v2.8
 * @see .cursor/plans/_overview/DOCUMENT_LAYER_STANDARD.md v1.5
 */

import type { BusinessDomain } from '@shared/types/business-domain';

import { GENESIS_POSTING_FLOW_IDS } from './genesis-posting-flow-ids';
import type {
  DocumentContextField,
  DocumentContextSchema,
  DocumentTypeMeta,
  ShellBlockId,
} from '@shared/document/org-document-types';

// ---------------------------------------------------------------------------
// Primitives (DocumentTypeMeta voucher slice)
// ---------------------------------------------------------------------------

export type DocumentFamily = 'voucher';
export type OrgDocumentPostingClass = 'posting';
export type ExecutionKind = 'accounting_stream';

export type { ShellBlockId };

export type VoucherCategory =
  | 'cash'
  | 'credit'
  | 'manual'
  | 'purchase_cycle'
  | 'sales_cycle'
  | 'warehouse'
  | 'production'
  | 'payroll'
  | 'fixed_asset'
  | 'finance_advanced'
  | 'legacy';

export type MatrixCat =
  | 'event'
  | 'finance'
  | 'finance_advanced'
  | 'entity'
  | 'item'
  | 'space'
  | 'manufacturing'
  | 'calendar';

export type SubdomainId = BusinessDomain;
export type MatrixRelationType = 'F' | 'R' | 'D' | 'T' | 'E';
export type VoucherLineMode = 'preset' | 'journal' | 'item' | 'none';
export type VoucherItemKind = 'sellable' | 'service' | 'consumable' | 'raw_material' | 'asset';

export type VoucherStreamType =
  | 'receipt'
  | 'expense'
  | 'sales_invoice'
  | 'purchase_invoice'
  | 'bank_transfer'
  | 'journal'
  | 'reversal'
  | 'inventory_adjustment'
  | 'counting_adjustment'
  | 'inventory_issue'
  | 'inventory_shrinkage_note'
  | 'inventory_transformation_note'
  | 'inventory_transfer_note'
  | 'purchase_order'
  | 'goods_receipt'
  | 'purchase_return'
  | 'sales_order'
  | 'delivery_note'
  | 'dispatch_note'
  | 'delivery_confirmation_note'
  | 'packing_note'
  | 'labeling_note'
  | 'incident_report'
  | 'maintenance_note'
  | 'collection_contact_note'
  | 'sales_return'
  | 'warehouse_transfer'
  | 'facility_charge'
  | 'production_receipt'
  | 'material_issue'
  | 'quality_release'
  | 'payroll'
  | 'severance_pay'
  | 'asset_acquisition'
  | 'depreciation'
  | 'asset_disposal'
  | 'scheduled_accrual'
  | 'fx_revaluation'
  | 'intercompany_elimination'
  | 'legal_entity_opening'
  | 'service_order'
  | 'service_delivery'
  | 'consumable_receipt'
  | 'consumable_issue'
  | 'consumable_adjustment'
  | 'service_invoice'
  | 'service_return'
  | 'raw_material_receipt'
  | 'raw_material_adjustment'
  | 'asset_transfer'
  | 'asset_revaluation'
  | 'material_return'
  | 'production_scrap';

export type VoucherContextField = DocumentContextField;

export interface VoucherContextSchema extends DocumentContextSchema {
  lineMode: VoucherLineMode;
}

export interface VoucherJournalPresetLine {
  accountCode: string;
  side: 'debit' | 'credit';
  labelKo?: string;
}

export interface VoucherSeedBalanceRules {
  presetLines: VoucherJournalPresetLine[];
  requiredEquation: string;
  autoCheck: boolean;
  tolerance: number;
  /** Preset 2-line 1:1 — single amount input mirrors to debit and credit */
  singleAmount?: boolean;
}

export interface VoucherSeedTemplate {
  balanceRules: VoucherSeedBalanceRules;
  executionPolicy: 'stop';
}

export interface VoucherAffects {
  ar?: boolean;
  ap?: boolean;
  cash?: boolean;
  inventory?: boolean;
}

export interface VoucherTypeMeta extends DocumentTypeMeta {
  documentType: VoucherStreamType;
  streamType: VoucherStreamType;
  family: 'voucher';
  postingClass: OrgDocumentPostingClass;
  executionKind: 'accounting_stream';
  category: VoucherCategory;
  phase: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  affects: VoucherAffects;
  matrixCats: MatrixCat[];
  writesSubdomains: SubdomainId[];
  readsSubdomains: SubdomainId[];
  contextSchema: VoucherContextSchema;
  requiresSourceVoucher?: boolean;
  requiresBudgetCheck?: boolean;
  requiresLegalEntity?: boolean;
  requiresApproval?: boolean;
  itemKinds?: VoucherItemKind[];
  seedTemplate?: VoucherSeedTemplate;
}

export interface CategoryProfile {
  primarySubdomain: SubdomainId;
  defaultLineMode: VoucherLineMode;
  matrixCats: MatrixCat[];
  shellBlocks: ShellBlockId[];
}

export interface DocumentFamilyManifestEntry {
  subdomainId: string;
  voucherRole?: 'read' | 'write' | 'both' | null;
}

export interface DocumentFamilyManifestLike {
  entries: DocumentFamilyManifestEntry[];
}

export interface RegistryValidationViolation {
  streamType: string;
  subdomainId: string;
  direction: 'read' | 'write';
  message: string;
}

export interface RegistryValidationResult {
  ok: boolean;
  violations: RegistryValidationViolation[];
}

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

export const VOUCHER_WRITE_SUBDOMAINS: SubdomainId[] = [
  'stream_execution',
  'finance_flow',
  'finance_state',
];

/** VoucherMatrixPostProcessor cross-domain writes (W2) */
const WRITES_SELLABLE_ITEM: SubdomainId[] = ['sellable_item'];
const WRITES_ITEM_AND_SPACE: SubdomainId[] = ['sellable_item', 'physical_space'];
const WRITES_CONSUMABLE_ITEM: SubdomainId[] = ['consumable_item'];
const WRITES_CONSUMABLE_AND_SPACE: SubdomainId[] = ['consumable_item', 'physical_space'];
const WRITES_RAW_MATERIAL_ITEM: SubdomainId[] = ['raw_material_item'];
const WRITES_RAW_MATERIAL_AND_SPACE: SubdomainId[] = ['raw_material_item', 'physical_space'];
const WRITES_ASSET_AND_SPACE: SubdomainId[] = ['asset_item', 'physical_space'];
const WRITES_VIRTUAL_SPACE: SubdomainId[] = ['virtual_space'];

const DEFAULT_SHELL_BLOCKS: ShellBlockId[] = ['header', 'context', 'lines', 'balance', 'actions'];

const ENTITY_FIELD: VoucherContextField = {
  key: 'entityId',
  labelKo: '거래처',
  subdomain: 'external_entity',
  relationTypes: ['R'],
  required: true,
};

const OPTIONAL_BUDGET_FIELD: VoucherContextField = {
  key: 'budgetCategory',
  labelKo: '예산 카테고리',
  subdomain: 'finance_budget',
  relationTypes: ['R'],
  required: false,
};

const OPTIONAL_ITEM_FIELD: VoucherContextField = {
  key: 'itemId',
  labelKo: '품목',
  subdomain: 'sellable_item',
  relationTypes: ['R'],
  required: false,
};

const OPTIONAL_SERVICE_ITEM_FIELD: VoucherContextField = {
  key: 'serviceItemId',
  labelKo: '서비스',
  subdomain: 'service_item',
  relationTypes: ['R'],
  required: false,
};

const OPTIONAL_CONSUMABLE_ITEM_FIELD: VoucherContextField = {
  key: 'consumableItemId',
  labelKo: '소모품',
  subdomain: 'consumable_item',
  relationTypes: ['R'],
  required: false,
};

const OPTIONAL_RAW_MATERIAL_ITEM_FIELD: VoucherContextField = {
  key: 'rawMaterialItemId',
  labelKo: '원자재',
  subdomain: 'raw_material_item',
  relationTypes: ['R'],
  required: false,
};

/** Finished-goods receipt slice — optional trace metadata only (not a completion gate). */
const OPTIONAL_PRODUCTION_ORDER_FIELD: VoucherContextField = {
  key: 'productionOrderId',
  labelKo: '생산 오더',
  subdomain: 'production_order',
  relationTypes: ['R'],
  required: false,
};

const LEGAL_ENTITY_FIELD: VoucherContextField = {
  key: 'legalEntityId',
  labelKo: '법인',
  subdomain: 'legal_entity',
  relationTypes: ['R'],
  required: true,
};

function balancePreset(
  debitCode: string,
  creditCode: string,
  debitLabelKo: string,
  creditLabelKo: string
): VoucherSeedTemplate {
  return {
    balanceRules: {
      presetLines: [
        { accountCode: debitCode, side: 'debit', labelKo: debitLabelKo },
        { accountCode: creditCode, side: 'credit', labelKo: creditLabelKo },
      ],
      requiredEquation: 'debit = credit',
      autoCheck: true,
      tolerance: 0.01,
      singleAmount: true,
    },
    executionPolicy: 'stop',
  };
}

function journalSeedTemplate(presetLines: VoucherJournalPresetLine[] = []): VoucherSeedTemplate {
  return {
    balanceRules: {
      presetLines,
      requiredEquation: 'debit = credit',
      autoCheck: true,
      tolerance: 0.01,
    },
    executionPolicy: 'stop',
  };
}

/** Phase 3+ item-cycle stub — GR/delivery/return/production lines */
function itemCycleStubSeed(
  debitCode: string,
  creditCode: string,
  debitLabelKo: string,
  creditLabelKo: string
): VoucherSeedTemplate {
  return balancePreset(debitCode, creditCode, debitLabelKo, creditLabelKo);
}

/** PO/SO/warehouse_transfer — header-only, no monetary lines */
function headerOnlyStubSeed(): VoucherSeedTemplate {
  return {
    balanceRules: {
      presetLines: [],
      requiredEquation: 'amount = 0',
      autoCheck: false,
      tolerance: 0,
    },
    executionPolicy: 'stop',
  };
}

function defaultStreamBind(): NonNullable<VoucherTypeMeta['streamBind']> {
  return {
    contextSelectionDerived: true,
    selectedFlowIds: [...GENESIS_POSTING_FLOW_IDS],
  };
}

export const CATEGORY_DEFAULTS: Record<VoucherCategory, CategoryProfile> = {
  cash: {
    primarySubdomain: 'external_entity',
    defaultLineMode: 'preset',
    matrixCats: ['finance', 'event', 'entity'],
    shellBlocks: DEFAULT_SHELL_BLOCKS,
  },
  credit: {
    primarySubdomain: 'external_entity',
    defaultLineMode: 'preset',
    matrixCats: ['finance', 'event', 'entity'],
    shellBlocks: DEFAULT_SHELL_BLOCKS,
  },
  manual: {
    primarySubdomain: 'finance_flow',
    defaultLineMode: 'journal',
    matrixCats: ['finance', 'event'],
    shellBlocks: DEFAULT_SHELL_BLOCKS,
  },
  purchase_cycle: {
    primarySubdomain: 'external_entity',
    defaultLineMode: 'none',
    matrixCats: ['finance', 'event', 'entity', 'item'],
    shellBlocks: DEFAULT_SHELL_BLOCKS,
  },
  sales_cycle: {
    primarySubdomain: 'external_entity',
    defaultLineMode: 'none',
    matrixCats: ['finance', 'event', 'entity', 'item'],
    shellBlocks: DEFAULT_SHELL_BLOCKS,
  },
  warehouse: {
    primarySubdomain: 'physical_space',
    defaultLineMode: 'item',
    matrixCats: ['item', 'space', 'finance', 'event'],
    shellBlocks: DEFAULT_SHELL_BLOCKS,
  },
  production: {
    primarySubdomain: 'production_order',
    defaultLineMode: 'item',
    matrixCats: ['manufacturing', 'item', 'finance', 'event'],
    shellBlocks: DEFAULT_SHELL_BLOCKS,
  },
  payroll: {
    primarySubdomain: 'internal_entity',
    defaultLineMode: 'preset',
    matrixCats: ['finance', 'entity', 'event'],
    shellBlocks: DEFAULT_SHELL_BLOCKS,
  },
  fixed_asset: {
    primarySubdomain: 'asset_item',
    defaultLineMode: 'journal',
    matrixCats: ['finance', 'item', 'event'],
    shellBlocks: DEFAULT_SHELL_BLOCKS,
  },
  finance_advanced: {
    primarySubdomain: 'legal_entity',
    defaultLineMode: 'journal',
    matrixCats: ['finance_advanced', 'finance', 'event'],
    shellBlocks: DEFAULT_SHELL_BLOCKS,
  },
  legacy: {
    primarySubdomain: 'asset_item',
    defaultLineMode: 'journal',
    matrixCats: ['finance', 'item'],
    shellBlocks: DEFAULT_SHELL_BLOCKS,
  },
};

interface VoucherTypeDef {
  streamType: VoucherStreamType;
  labelKo: string;
  category: VoucherCategory;
  phase: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  affects?: VoucherAffects;
  matrixCats?: MatrixCat[];
  readsSubdomains: SubdomainId[];
  contextFields: VoucherContextField[];
  lineMode?: VoucherLineMode;
  primarySubdomain?: SubdomainId;
  requiresBudgetCheck?: boolean;
  requiresSourceVoucher?: boolean;
  requiresLegalEntity?: boolean;
  requiresApproval?: boolean;
  itemKinds?: VoucherItemKind[];
  seedTemplate?: VoucherSeedTemplate;
  streamTemplateId?: string;
  streamBind?: VoucherTypeMeta['streamBind'];
  extraWritesSubdomains?: SubdomainId[];
}

function uniqueSubdomains(ids: SubdomainId[]): SubdomainId[] {
  return [...new Set(ids)];
}

export function buildVoucherTypeMeta(def: VoucherTypeDef): VoucherTypeMeta {
  const profile = CATEGORY_DEFAULTS[def.category];
  const lineMode = def.lineMode ?? profile.defaultLineMode;
  const primarySubdomain = def.primarySubdomain ?? profile.primarySubdomain;

  const meta: VoucherTypeMeta = {
    documentType: def.streamType,
    streamType: def.streamType,
    family: 'voucher',
    postingClass: 'posting',
    executionKind: 'accounting_stream',
    labelKo: def.labelKo,
    primarySubdomain,
    shellBlocks: [...profile.shellBlocks],
    category: def.category,
    phase: def.phase,
    affects: def.affects ?? {},
    matrixCats: def.matrixCats ?? profile.matrixCats,
    writesSubdomains: uniqueSubdomains([
      ...VOUCHER_WRITE_SUBDOMAINS,
      ...(def.extraWritesSubdomains ?? []),
    ]),
    readsSubdomains: uniqueSubdomains(def.readsSubdomains),
    contextSchema: {
      primarySubdomain,
      fields: def.contextFields,
      lineMode,
    },
  };

  if (def.requiresBudgetCheck) meta.requiresBudgetCheck = true;
  if (def.requiresSourceVoucher) meta.requiresSourceVoucher = true;
  if (def.requiresLegalEntity) meta.requiresLegalEntity = true;
  if (def.requiresApproval) meta.requiresApproval = true;
  if (def.itemKinds) meta.itemKinds = def.itemKinds;
  if (def.seedTemplate) meta.seedTemplate = def.seedTemplate;
  if (def.streamTemplateId) meta.streamTemplateId = def.streamTemplateId;
  if (def.streamBind) meta.streamBind = def.streamBind;

  return meta;
}

function streamTemplateIdFor(streamType: VoucherStreamType): string {
  return `stream_template_voucher_${streamType}`;
}

const VOUCHER_TYPE_DEFS: VoucherTypeDef[] = [
  // Phase 1 — seed + UI
  {
    streamType: 'receipt',
    labelKo: '입금보고서',
    category: 'cash',
    phase: 1,
    affects: { cash: true },
    readsSubdomains: ['external_entity'],
    contextFields: [ENTITY_FIELD],
    lineMode: 'preset',
    requiresApproval: true,
    seedTemplate: balancePreset('1010', '1020', '매출채권', '받을어음'),
    streamTemplateId: streamTemplateIdFor('receipt'),
    streamBind: {
      contextSelectionDerived: true,
      selectedFlowIds: [...GENESIS_POSTING_FLOW_IDS],
    },
  },
  {
    streamType: 'expense',
    labelKo: '지출결의서',
    category: 'cash',
    phase: 1,
    affects: { cash: true },
    readsSubdomains: ['external_entity', 'finance_budget'],
    contextFields: [ENTITY_FIELD, OPTIONAL_BUDGET_FIELD],
    lineMode: 'preset',
    requiresBudgetCheck: true,
    requiresApproval: true,
    seedTemplate: balancePreset('5000', '1010', '원재료비', '매출채권'),
    streamTemplateId: streamTemplateIdFor('expense'),
    streamBind: {
      contextSelectionDerived: true,
      selectedFlowIds: [...GENESIS_POSTING_FLOW_IDS],
    },
  },
  {
    streamType: 'sales_invoice',
    labelKo: '매출전표 I',
    category: 'credit',
    phase: 1,
    affects: { ar: true },
    readsSubdomains: ['external_entity', 'sellable_item', 'service_item'],
    contextFields: [ENTITY_FIELD, OPTIONAL_ITEM_FIELD, OPTIONAL_SERVICE_ITEM_FIELD],
    lineMode: 'preset',
    itemKinds: ['sellable', 'service'],
    requiresApproval: true,
    seedTemplate: balancePreset('1020', '4000', '받을어음', '매출'),
    streamTemplateId: streamTemplateIdFor('sales_invoice'),
    streamBind: {
      contextSelectionDerived: true,
      selectedFlowIds: [...GENESIS_POSTING_FLOW_IDS],
    },
  },
  {
    streamType: 'purchase_invoice',
    labelKo: '매입전표 I',
    category: 'credit',
    phase: 1,
    affects: { ap: true },
    readsSubdomains: ['external_entity', 'sellable_item', 'finance_budget'],
    contextFields: [ENTITY_FIELD, OPTIONAL_ITEM_FIELD, OPTIONAL_BUDGET_FIELD],
    lineMode: 'preset',
    requiresBudgetCheck: true,
    requiresApproval: true,
    seedTemplate: balancePreset('1030', '2010', '재고자산', '지급어음'),
    streamTemplateId: streamTemplateIdFor('purchase_invoice'),
    streamBind: {
      contextSelectionDerived: true,
      selectedFlowIds: [...GENESIS_POSTING_FLOW_IDS],
    },
  },
  // Phase 2 — seed + UI (Stream E)
  {
    streamType: 'bank_transfer',
    labelKo: '계좌이동',
    category: 'manual',
    phase: 2,
    affects: { cash: true },
    matrixCats: ['finance', 'event'],
    requiresApproval: true,
    readsSubdomains: ['finance_state'],
    contextFields: [
      {
        key: 'sourceAccountId',
        labelKo: '출금 계정',
        subdomain: 'finance_state',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'targetAccountId',
        labelKo: '입금 계정',
        subdomain: 'finance_state',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'journal',
    seedTemplate: journalSeedTemplate([
      { accountCode: '1050', side: 'debit', labelKo: '입금 계좌' },
      { accountCode: '1040', side: 'credit', labelKo: '출금 계좌' },
    ]),
    streamTemplateId: streamTemplateIdFor('bank_transfer'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'journal',
    labelKo: '일반전표',
    category: 'manual',
    phase: 2,
    matrixCats: ['finance', 'event'],
    requiresApproval: true,
    readsSubdomains: [],
    contextFields: [],
    lineMode: 'journal',
    primarySubdomain: 'finance_flow',
    seedTemplate: journalSeedTemplate(),
    streamTemplateId: streamTemplateIdFor('journal'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'reversal',
    labelKo: '역분개',
    category: 'manual',
    phase: 2,
    matrixCats: ['finance', 'event'],
    readsSubdomains: ['stream_execution'],
    contextFields: [
      {
        key: 'sourceVoucherId',
        labelKo: '원전표',
        subdomain: 'stream_execution',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'journal',
    requiresSourceVoucher: true,
    requiresApproval: true,
    primarySubdomain: 'stream_execution',
    seedTemplate: journalSeedTemplate(),
    streamTemplateId: streamTemplateIdFor('reversal'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'inventory_adjustment',
    labelKo: '재고조정',
    category: 'warehouse',
    phase: 2,
    affects: { inventory: true },
    matrixCats: ['item', 'space', 'finance'],
    extraWritesSubdomains: WRITES_ITEM_AND_SPACE,
    readsSubdomains: ['physical_space', 'sellable_item'],
    contextFields: [
      {
        key: 'warehouseId',
        labelKo: '창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      ENTITY_FIELD,
    ],
    lineMode: 'journal',
    itemKinds: ['sellable'],
    seedTemplate: journalSeedTemplate([
      { accountCode: '1030', side: 'debit', labelKo: '재고자산' },
      { accountCode: '5900', side: 'credit', labelKo: '재고조정' },
    ]),
    streamTemplateId: streamTemplateIdFor('inventory_adjustment'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'counting_adjustment',
    labelKo: '실사조정',
    category: 'warehouse',
    phase: 2,
    affects: { inventory: true },
    matrixCats: ['item', 'space'],
    extraWritesSubdomains: WRITES_ITEM_AND_SPACE,
    readsSubdomains: ['physical_space', 'sellable_item'],
    contextFields: [
      {
        key: 'itemId',
        labelKo: '품목',
        subdomain: 'sellable_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'warehouseId',
        labelKo: '창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'entityId',
        labelKo: '거래처',
        subdomain: 'external_entity',
        relationTypes: ['R'],
        required: false,
      },
      {
        key: 'countedQuantity',
        labelKo: '실사수량',
        subdomain: 'sellable_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'systemQuantity',
        labelKo: '시스템수량',
        subdomain: 'sellable_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'adjustmentReason',
        labelKo: '조정사유',
        subdomain: 'stream_execution',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'none',
    itemKinds: ['sellable'],
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('counting_adjustment'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'inventory_issue',
    labelKo: '재고출고',
    category: 'warehouse',
    phase: 2,
    affects: { inventory: true },
    matrixCats: ['item', 'space'],
    extraWritesSubdomains: WRITES_ITEM_AND_SPACE,
    readsSubdomains: ['physical_space', 'sellable_item'],
    contextFields: [
      {
        key: 'itemId',
        labelKo: '품목',
        subdomain: 'sellable_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'warehouseId',
        labelKo: '창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'quantity',
        labelKo: '출고수량',
        subdomain: 'sellable_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'issueReason',
        labelKo: '출고사유',
        subdomain: 'stream_execution',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'none',
    itemKinds: ['sellable'],
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('inventory_issue'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'inventory_shrinkage_note',
    labelKo: '재고손실전표',
    category: 'warehouse',
    phase: 2,
    affects: { inventory: true },
    matrixCats: ['item', 'space'],
    extraWritesSubdomains: WRITES_ITEM_AND_SPACE,
    readsSubdomains: ['physical_space', 'sellable_item'],
    contextFields: [
      {
        key: 'itemId',
        labelKo: '품목',
        subdomain: 'sellable_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'warehouseId',
        labelKo: '창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'quantity',
        labelKo: '손실수량',
        subdomain: 'sellable_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'reasonKind',
        labelKo: '손실사유',
        subdomain: 'stream_execution',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'note',
        labelKo: '감독자 메모',
        subdomain: 'stream_execution',
        relationTypes: ['R'],
        required: false,
      },
    ],
    lineMode: 'none',
    itemKinds: ['sellable'],
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('inventory_shrinkage_note'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'inventory_transformation_note',
    labelKo: '재고변환전표',
    category: 'warehouse',
    phase: 2,
    affects: { inventory: true },
    matrixCats: ['item', 'space'],
    extraWritesSubdomains: WRITES_ITEM_AND_SPACE,
    readsSubdomains: ['physical_space', 'sellable_item'],
    contextFields: [
      {
        key: 'inputItemId',
        labelKo: '투입 품목',
        subdomain: 'sellable_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'outputItemId',
        labelKo: '산출 품목',
        subdomain: 'sellable_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'warehouseId',
        labelKo: '창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'inputQuantity',
        labelKo: '투입 수량',
        subdomain: 'sellable_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'outputQuantity',
        labelKo: '산출 수량',
        subdomain: 'sellable_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'transformationKind',
        labelKo: '변환 유형',
        subdomain: 'stream_execution',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'note',
        labelKo: '메모',
        subdomain: 'stream_execution',
        relationTypes: ['R'],
        required: false,
      },
    ],
    lineMode: 'none',
    itemKinds: ['sellable'],
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('inventory_transformation_note'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'inventory_transfer_note',
    labelKo: '재고이동전표',
    category: 'warehouse',
    phase: 2,
    affects: { inventory: true },
    matrixCats: ['item', 'space'],
    extraWritesSubdomains: WRITES_ITEM_AND_SPACE,
    readsSubdomains: ['physical_space', 'sellable_item'],
    contextFields: [
      {
        key: 'itemId',
        labelKo: '품목',
        subdomain: 'sellable_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'sourceSpaceId',
        labelKo: '출발 공간',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'destinationSpaceId',
        labelKo: '도착 공간',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'quantity',
        labelKo: '이동 수량',
        subdomain: 'sellable_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'unit',
        labelKo: '단위',
        subdomain: 'sellable_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'transferReason',
        labelKo: '이동 사유',
        subdomain: 'stream_execution',
        relationTypes: ['R'],
        required: false,
      },
      {
        key: 'note',
        labelKo: '메모',
        subdomain: 'stream_execution',
        relationTypes: ['R'],
        required: false,
      },
      {
        key: 'sourceWarehouseId',
        labelKo: '출발 창고 (표시용)',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: false,
      },
      {
        key: 'destinationWarehouseId',
        labelKo: '도착 창고 (표시용)',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: false,
      },
    ],
    lineMode: 'none',
    itemKinds: ['sellable'],
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('inventory_transfer_note'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'facility_charge',
    labelKo: '공간 운영비',
    category: 'warehouse',
    phase: 2,
    matrixCats: ['space', 'finance', 'event'],
    extraWritesSubdomains: WRITES_VIRTUAL_SPACE,
    readsSubdomains: ['virtual_space'],
    primarySubdomain: 'virtual_space',
    contextFields: [
      {
        key: 'virtualSpaceId',
        labelKo: '가상 공간',
        subdomain: 'virtual_space',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'journal',
    seedTemplate: journalSeedTemplate([
      { accountCode: '6010', side: 'debit', labelKo: '임차료' },
      { accountCode: '1010', side: 'credit', labelKo: '현금' },
    ]),
    streamTemplateId: streamTemplateIdFor('facility_charge'),
    streamBind: defaultStreamBind(),
  },
  // Phase 3 — inventory cycle seed stubs (Stream F)
  {
    streamType: 'purchase_order',
    labelKo: '발주서',
    category: 'purchase_cycle',
    phase: 3,
    affects: { ap: true, inventory: false },
    readsSubdomains: ['external_entity'],
    contextFields: [ENTITY_FIELD],
    lineMode: 'none',
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('purchase_order'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'goods_receipt',
    labelKo: '구매입고 (GR)',
    category: 'purchase_cycle',
    phase: 3,
    affects: { inventory: true, ap: true },
    extraWritesSubdomains: WRITES_ITEM_AND_SPACE,
    readsSubdomains: ['external_entity', 'physical_space', 'sellable_item', 'raw_material_item'],
    contextFields: [
      ENTITY_FIELD,
      {
        key: 'warehouseId',
        labelKo: '입고 창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'item',
    itemKinds: ['sellable', 'raw_material'],
    requiresApproval: true,
    seedTemplate: itemCycleStubSeed('1030', '2010', '재고자산', '매입채무'),
    streamTemplateId: streamTemplateIdFor('goods_receipt'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'purchase_return',
    labelKo: '매입반품',
    category: 'purchase_cycle',
    phase: 3,
    affects: { inventory: true, ap: true },
    extraWritesSubdomains: WRITES_SELLABLE_ITEM,
    readsSubdomains: ['external_entity', 'sellable_item'],
    contextFields: [ENTITY_FIELD],
    lineMode: 'item',
    itemKinds: ['sellable'],
    seedTemplate: itemCycleStubSeed('2010', '1030', '매입채무', '재고자산'),
    streamTemplateId: streamTemplateIdFor('purchase_return'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'sales_order',
    labelKo: '주문서 (SO)',
    category: 'sales_cycle',
    phase: 3,
    affects: { ar: true, inventory: false },
    readsSubdomains: ['external_entity'],
    contextFields: [ENTITY_FIELD],
    lineMode: 'none',
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('sales_order'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'delivery_note',
    labelKo: '판매출고',
    category: 'sales_cycle',
    phase: 3,
    affects: { inventory: true, ar: true },
    extraWritesSubdomains: WRITES_ITEM_AND_SPACE,
    readsSubdomains: ['external_entity', 'physical_space', 'sellable_item'],
    contextFields: [
      ENTITY_FIELD,
      {
        key: 'warehouseId',
        labelKo: '출고 창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'item',
    itemKinds: ['sellable'],
    requiresApproval: true,
    seedTemplate: itemCycleStubSeed('1020', '1030', '매출채권', '재고자산'),
    streamTemplateId: streamTemplateIdFor('delivery_note'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'dispatch_note',
    labelKo: '발송 증빙서',
    category: 'sales_cycle',
    phase: 3,
    affects: { inventory: false },
    readsSubdomains: ['external_entity', 'stream_execution'],
    contextFields: [ENTITY_FIELD],
    lineMode: 'none',
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('dispatch_note'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'delivery_confirmation_note',
    labelKo: '배송 완료 증빙서',
    category: 'sales_cycle',
    phase: 3,
    affects: { inventory: false },
    readsSubdomains: ['external_entity', 'stream_execution'],
    contextFields: [ENTITY_FIELD],
    lineMode: 'none',
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('delivery_confirmation_note'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'packing_note',
    labelKo: '포장 증빙서',
    category: 'warehouse',
    phase: 1,
    affects: { inventory: false },
    readsSubdomains: ['physical_space', 'stream_execution'],
    contextFields: [ENTITY_FIELD],
    lineMode: 'none',
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('packing_note'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'labeling_note',
    labelKo: '라벨링 증빙서',
    category: 'warehouse',
    phase: 1,
    affects: { inventory: false },
    readsSubdomains: ['physical_space', 'stream_execution'],
    contextFields: [ENTITY_FIELD],
    lineMode: 'none',
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('labeling_note'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'incident_report',
    labelKo: '문제 보고서',
    category: 'warehouse',
    phase: 1,
    affects: { inventory: false },
    readsSubdomains: ['physical_space', 'stream_execution'],
    contextFields: [ENTITY_FIELD],
    lineMode: 'none',
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('incident_report'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'maintenance_note',
    labelKo: '청소/점검 기록',
    category: 'warehouse',
    phase: 1,
    affects: { inventory: false },
    readsSubdomains: ['physical_space', 'stream_execution'],
    contextFields: [ENTITY_FIELD],
    lineMode: 'none',
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('maintenance_note'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'collection_contact_note',
    labelKo: '채권 회수 연락 기록',
    category: 'credit',
    phase: 1,
    affects: { inventory: false },
    readsSubdomains: ['external_entity', 'stream_execution'],
    contextFields: [ENTITY_FIELD],
    lineMode: 'none',
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('collection_contact_note'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'sales_return',
    labelKo: '매출반품',
    category: 'sales_cycle',
    phase: 3,
    affects: { inventory: true, ar: true },
    extraWritesSubdomains: WRITES_SELLABLE_ITEM,
    readsSubdomains: ['external_entity', 'sellable_item'],
    contextFields: [ENTITY_FIELD],
    lineMode: 'item',
    itemKinds: ['sellable'],
    seedTemplate: itemCycleStubSeed('1030', '1020', '재고자산', '매출채권'),
    streamTemplateId: streamTemplateIdFor('sales_return'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'warehouse_transfer',
    labelKo: '창고이동',
    category: 'warehouse',
    phase: 3,
    affects: { inventory: true },
    extraWritesSubdomains: ['physical_space'],
    readsSubdomains: ['physical_space', 'sellable_item'],
    contextFields: [
      {
        key: 'sourceWarehouseId',
        labelKo: '출고 창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'targetWarehouseId',
        labelKo: '입고 창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'item',
    itemKinds: ['sellable'],
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('warehouse_transfer'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'production_receipt',
    labelKo: '생산입고',
    category: 'production',
    phase: 3,
    affects: { inventory: true },
    extraWritesSubdomains: WRITES_SELLABLE_ITEM,
    readsSubdomains: ['physical_space', 'production_order', 'sellable_item'],
    contextFields: [
      OPTIONAL_PRODUCTION_ORDER_FIELD,
      {
        key: 'warehouseId',
        labelKo: '입고 창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'item',
    itemKinds: ['sellable'],
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('production_receipt'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'material_issue',
    labelKo: '자재출고 (BOM)',
    category: 'production',
    phase: 3,
    affects: { inventory: true },
    readsSubdomains: ['manufacturing_bom', 'raw_material_item'],
    contextFields: [
      {
        key: 'bomId',
        labelKo: 'BOM',
        subdomain: 'manufacturing_bom',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'item',
    itemKinds: ['raw_material'],
    seedTemplate: itemCycleStubSeed('5100', '1030', '제조원가', '재고자산'),
    streamTemplateId: streamTemplateIdFor('material_issue'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'quality_release',
    labelKo: '품질 승인 입고',
    category: 'production',
    phase: 3,
    affects: { inventory: true },
    extraWritesSubdomains: WRITES_SELLABLE_ITEM,
    readsSubdomains: ['quality_inspection', 'sellable_item'],
    contextFields: [
      {
        key: 'inspectionId',
        labelKo: '품질 검사',
        subdomain: 'quality_inspection',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'item',
    itemKinds: ['sellable'],
    seedTemplate: itemCycleStubSeed('1030', '5200', '재고자산', '품질보류'),
    streamTemplateId: streamTemplateIdFor('quality_release'),
    streamBind: defaultStreamBind(),
  },
  // Phase 4 — payroll & fixed asset seed stubs (Stream G)
  {
    streamType: 'payroll',
    labelKo: '급여명세서',
    category: 'payroll',
    phase: 4,
    readsSubdomains: ['internal_entity', 'finance_flow'],
    contextFields: [
      {
        key: 'employeeId',
        labelKo: '직원',
        subdomain: 'internal_entity',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'preset',
    seedTemplate: balancePreset('6100', '1010', '급여', '현금'),
    streamTemplateId: streamTemplateIdFor('payroll'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'severance_pay',
    labelKo: '퇴직금',
    category: 'payroll',
    phase: 4,
    readsSubdomains: ['internal_entity'],
    contextFields: [
      {
        key: 'employeeId',
        labelKo: '직원',
        subdomain: 'internal_entity',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'preset',
    seedTemplate: balancePreset('6200', '1010', '퇴직급여', '현금'),
    streamTemplateId: streamTemplateIdFor('severance_pay'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'asset_acquisition',
    labelKo: '자산취득',
    category: 'fixed_asset',
    phase: 4,
    readsSubdomains: ['asset_item', 'finance_state'],
    contextFields: [
      {
        key: 'assetId',
        labelKo: '자산',
        subdomain: 'asset_item',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'journal',
    itemKinds: ['asset'],
    seedTemplate: journalSeedTemplate([
      { accountCode: '1500', side: 'debit', labelKo: '유형자산' },
      { accountCode: '1010', side: 'credit', labelKo: '현금' },
    ]),
    streamTemplateId: streamTemplateIdFor('asset_acquisition'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'depreciation',
    labelKo: '감가상각',
    category: 'fixed_asset',
    phase: 4,
    matrixCats: ['finance', 'calendar', 'item'],
    readsSubdomains: ['asset_item', 'calendar_trigger'],
    contextFields: [
      {
        key: 'assetId',
        labelKo: '자산',
        subdomain: 'asset_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'scheduleId',
        labelKo: '정기 일정',
        subdomain: 'calendar_trigger',
        relationTypes: ['T'],
        required: false,
      },
    ],
    lineMode: 'preset',
    itemKinds: ['asset'],
    seedTemplate: balancePreset('6300', '1510', '감가상각비', '감가상각누계액'),
    streamTemplateId: streamTemplateIdFor('depreciation'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'asset_disposal',
    labelKo: '자산처분',
    category: 'fixed_asset',
    phase: 4,
    readsSubdomains: ['asset_item'],
    contextFields: [
      {
        key: 'assetId',
        labelKo: '자산',
        subdomain: 'asset_item',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'journal',
    itemKinds: ['asset'],
    seedTemplate: journalSeedTemplate([
      { accountCode: '1010', side: 'debit', labelKo: '처분대금' },
      { accountCode: '1500', side: 'credit', labelKo: '유형자산' },
    ]),
    streamTemplateId: streamTemplateIdFor('asset_disposal'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'scheduled_accrual',
    labelKo: '정기 accrual',
    category: 'fixed_asset',
    phase: 4,
    matrixCats: ['finance', 'calendar'],
    readsSubdomains: ['calendar_trigger', 'finance_flow'],
    contextFields: [
      {
        key: 'scheduleId',
        labelKo: '정기 일정',
        subdomain: 'calendar_trigger',
        relationTypes: ['T'],
        required: true,
      },
    ],
    lineMode: 'preset',
    seedTemplate: balancePreset('6400', '2010', '미지급비용', '매입채무'),
    streamTemplateId: streamTemplateIdFor('scheduled_accrual'),
    streamBind: defaultStreamBind(),
  },
  // Phase 5 — finance_advanced seed stubs (Stream H)
  {
    streamType: 'fx_revaluation',
    labelKo: '환율 재평가',
    category: 'finance_advanced',
    phase: 5,
    matrixCats: ['finance_advanced', 'finance'],
    readsSubdomains: ['currency_rate', 'finance_state', 'finance_flow', 'legal_entity'],
    contextFields: [
      LEGAL_ENTITY_FIELD,
      {
        key: 'rateId',
        labelKo: '환율',
        subdomain: 'currency_rate',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'journal',
    requiresLegalEntity: true,
    requiresApproval: true,
    seedTemplate: journalSeedTemplate([
      { accountCode: '1020', side: 'debit', labelKo: '외화채권' },
      { accountCode: '7100', side: 'credit', labelKo: '환율차익' },
    ]),
    streamTemplateId: streamTemplateIdFor('fx_revaluation'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'intercompany_elimination',
    labelKo: '내부거래 상쇄',
    category: 'finance_advanced',
    phase: 5,
    matrixCats: ['finance_advanced'],
    readsSubdomains: ['intercompany', 'legal_entity', 'finance_flow'],
    contextFields: [
      LEGAL_ENTITY_FIELD,
      {
        key: 'intercompanyId',
        labelKo: '내부거래',
        subdomain: 'intercompany',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'journal',
    requiresLegalEntity: true,
    requiresApproval: true,
    seedTemplate: journalSeedTemplate([
      { accountCode: '1020', side: 'debit', labelKo: '내부채권' },
      { accountCode: '2010', side: 'credit', labelKo: '내부채무' },
    ]),
    streamTemplateId: streamTemplateIdFor('intercompany_elimination'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'legal_entity_opening',
    labelKo: '법인 기초잔액',
    category: 'finance_advanced',
    phase: 5,
    matrixCats: ['finance_advanced'],
    readsSubdomains: ['legal_entity', 'finance_state'],
    contextFields: [LEGAL_ENTITY_FIELD],
    lineMode: 'journal',
    requiresLegalEntity: true,
    requiresApproval: true,
    seedTemplate: journalSeedTemplate([
      { accountCode: '1010', side: 'debit', labelKo: '현금' },
      { accountCode: '3000', side: 'credit', labelKo: '자본금' },
    ]),
    streamTemplateId: streamTemplateIdFor('legal_entity_opening'),
    streamBind: defaultStreamBind(),
  },
  // Phase 6 — service_item · consumable_item cycle seed stubs (Stream I)
  {
    streamType: 'service_order',
    labelKo: '서비스 주문서',
    category: 'sales_cycle',
    phase: 6,
    affects: { ar: true, inventory: false },
    readsSubdomains: ['external_entity', 'service_item'],
    contextFields: [ENTITY_FIELD, OPTIONAL_SERVICE_ITEM_FIELD],
    lineMode: 'none',
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('service_order'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'service_delivery',
    labelKo: '서비스 제공',
    category: 'sales_cycle',
    phase: 6,
    affects: { ar: true, inventory: false },
    readsSubdomains: ['external_entity', 'service_item'],
    contextFields: [ENTITY_FIELD, OPTIONAL_SERVICE_ITEM_FIELD],
    lineMode: 'item',
    itemKinds: ['service'],
    requiresApproval: true,
    seedTemplate: itemCycleStubSeed('1020', '4000', '매출채권', '용역매출'),
    streamTemplateId: streamTemplateIdFor('service_delivery'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'consumable_receipt',
    labelKo: '소모품 입고',
    category: 'purchase_cycle',
    phase: 6,
    affects: { inventory: true, ap: true },
    extraWritesSubdomains: WRITES_CONSUMABLE_AND_SPACE,
    readsSubdomains: ['external_entity', 'physical_space', 'consumable_item'],
    contextFields: [
      ENTITY_FIELD,
      {
        key: 'warehouseId',
        labelKo: '입고 창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      OPTIONAL_CONSUMABLE_ITEM_FIELD,
    ],
    lineMode: 'item',
    itemKinds: ['consumable'],
    requiresApproval: true,
    seedTemplate: itemCycleStubSeed('1030', '2010', '소모품재고', '매입채무'),
    streamTemplateId: streamTemplateIdFor('consumable_receipt'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'consumable_issue',
    labelKo: '소모품 출고',
    category: 'warehouse',
    phase: 6,
    affects: { inventory: true },
    extraWritesSubdomains: WRITES_CONSUMABLE_AND_SPACE,
    readsSubdomains: ['physical_space', 'consumable_item'],
    contextFields: [
      {
        key: 'warehouseId',
        labelKo: '출고 창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      OPTIONAL_CONSUMABLE_ITEM_FIELD,
    ],
    lineMode: 'item',
    itemKinds: ['consumable'],
    seedTemplate: itemCycleStubSeed('6010', '1030', '소모품비', '소모품재고'),
    streamTemplateId: streamTemplateIdFor('consumable_issue'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'consumable_adjustment',
    labelKo: '소모품 재고조정',
    category: 'warehouse',
    phase: 6,
    affects: { inventory: true },
    extraWritesSubdomains: WRITES_CONSUMABLE_ITEM,
    readsSubdomains: ['physical_space', 'consumable_item'],
    contextFields: [
      {
        key: 'warehouseId',
        labelKo: '창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      OPTIONAL_CONSUMABLE_ITEM_FIELD,
    ],
    lineMode: 'item',
    itemKinds: ['consumable'],
    seedTemplate: itemCycleStubSeed('1030', '6010', '소모품재고', '소모품비'),
    streamTemplateId: streamTemplateIdFor('consumable_adjustment'),
    streamBind: defaultStreamBind(),
  },
  // Phase 7 — service_item · raw_material_item cycle seed stubs (Stream I+)
  {
    streamType: 'service_invoice',
    labelKo: '서비스 매출전표',
    category: 'sales_cycle',
    phase: 7,
    affects: { ar: true, inventory: false },
    readsSubdomains: ['external_entity', 'service_item'],
    contextFields: [ENTITY_FIELD, OPTIONAL_SERVICE_ITEM_FIELD],
    lineMode: 'preset',
    itemKinds: ['service'],
    requiresApproval: true,
    seedTemplate: balancePreset('1020', '4000', '매출채권', '용역매출'),
    streamTemplateId: streamTemplateIdFor('service_invoice'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'service_return',
    labelKo: '서비스 반품',
    category: 'sales_cycle',
    phase: 7,
    affects: { ar: true, inventory: false },
    readsSubdomains: ['external_entity', 'service_item'],
    contextFields: [ENTITY_FIELD, OPTIONAL_SERVICE_ITEM_FIELD],
    lineMode: 'item',
    itemKinds: ['service'],
    requiresApproval: true,
    seedTemplate: itemCycleStubSeed('4000', '1020', '용역매출', '매출채권'),
    streamTemplateId: streamTemplateIdFor('service_return'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'raw_material_receipt',
    labelKo: '원자재 입고',
    category: 'purchase_cycle',
    phase: 7,
    affects: { inventory: true, ap: true },
    extraWritesSubdomains: WRITES_RAW_MATERIAL_AND_SPACE,
    readsSubdomains: ['external_entity', 'physical_space', 'raw_material_item'],
    contextFields: [
      ENTITY_FIELD,
      {
        key: 'warehouseId',
        labelKo: '입고 창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      OPTIONAL_RAW_MATERIAL_ITEM_FIELD,
    ],
    lineMode: 'item',
    itemKinds: ['raw_material'],
    requiresApproval: true,
    seedTemplate: itemCycleStubSeed('1040', '2010', '원자재재고', '매입채무'),
    streamTemplateId: streamTemplateIdFor('raw_material_receipt'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'raw_material_adjustment',
    labelKo: '원자재 재고조정',
    category: 'warehouse',
    phase: 7,
    affects: { inventory: true },
    extraWritesSubdomains: WRITES_RAW_MATERIAL_ITEM,
    readsSubdomains: ['physical_space', 'raw_material_item'],
    contextFields: [
      {
        key: 'warehouseId',
        labelKo: '창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      OPTIONAL_RAW_MATERIAL_ITEM_FIELD,
    ],
    lineMode: 'item',
    itemKinds: ['raw_material'],
    seedTemplate: itemCycleStubSeed('1040', '5900', '원자재재고', '재고조정'),
    streamTemplateId: streamTemplateIdFor('raw_material_adjustment'),
    streamBind: defaultStreamBind(),
  },
  // Phase 8 — asset_item · manufacturing BOM cycle seed stubs (Stream I++)
  {
    streamType: 'asset_transfer',
    labelKo: '자산이동',
    category: 'fixed_asset',
    phase: 8,
    affects: { inventory: false },
    extraWritesSubdomains: WRITES_ASSET_AND_SPACE,
    readsSubdomains: ['physical_space', 'asset_item'],
    contextFields: [
      {
        key: 'assetId',
        labelKo: '자산',
        subdomain: 'asset_item',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'sourceWarehouseId',
        labelKo: '출고 창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'targetWarehouseId',
        labelKo: '입고 창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'item',
    itemKinds: ['asset'],
    requiresApproval: true,
    seedTemplate: headerOnlyStubSeed(),
    streamTemplateId: streamTemplateIdFor('asset_transfer'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'asset_revaluation',
    labelKo: '자산 재평가',
    category: 'fixed_asset',
    phase: 8,
    readsSubdomains: ['asset_item', 'finance_state'],
    contextFields: [
      {
        key: 'assetId',
        labelKo: '자산',
        subdomain: 'asset_item',
        relationTypes: ['R'],
        required: true,
      },
    ],
    lineMode: 'journal',
    itemKinds: ['asset'],
    requiresApproval: true,
    seedTemplate: journalSeedTemplate([
      { accountCode: '5900', side: 'debit', labelKo: '손상차손' },
      { accountCode: '1500', side: 'credit', labelKo: '유형자산' },
    ]),
    streamTemplateId: streamTemplateIdFor('asset_revaluation'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'material_return',
    labelKo: '자재 반납',
    category: 'production',
    phase: 8,
    affects: { inventory: true },
    extraWritesSubdomains: WRITES_RAW_MATERIAL_AND_SPACE,
    readsSubdomains: ['manufacturing_bom', 'raw_material_item', 'physical_space'],
    contextFields: [
      {
        key: 'bomId',
        labelKo: 'BOM',
        subdomain: 'manufacturing_bom',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'warehouseId',
        labelKo: '반납 창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      OPTIONAL_RAW_MATERIAL_ITEM_FIELD,
    ],
    lineMode: 'item',
    itemKinds: ['raw_material'],
    requiresApproval: true,
    seedTemplate: itemCycleStubSeed('1040', '5100', '원자재재고', '제조원가'),
    streamTemplateId: streamTemplateIdFor('material_return'),
    streamBind: defaultStreamBind(),
  },
  {
    streamType: 'production_scrap',
    labelKo: '생산 스크랩',
    category: 'production',
    phase: 8,
    affects: { inventory: true },
    extraWritesSubdomains: WRITES_RAW_MATERIAL_AND_SPACE,
    readsSubdomains: ['production_order', 'raw_material_item', 'physical_space'],
    contextFields: [
      {
        key: 'productionOrderId',
        labelKo: '생산 오더',
        subdomain: 'production_order',
        relationTypes: ['R'],
        required: true,
      },
      {
        key: 'warehouseId',
        labelKo: '스크랩 창고',
        subdomain: 'physical_space',
        relationTypes: ['R'],
        required: true,
      },
      OPTIONAL_RAW_MATERIAL_ITEM_FIELD,
    ],
    lineMode: 'item',
    itemKinds: ['raw_material'],
    requiresApproval: true,
    seedTemplate: itemCycleStubSeed('5900', '1040', '스크랩손실', '원자재재고'),
    streamTemplateId: streamTemplateIdFor('production_scrap'),
    streamBind: defaultStreamBind(),
  },
];

export const VOUCHER_TYPE_REGISTRY: Record<VoucherStreamType, VoucherTypeMeta> =
  VOUCHER_TYPE_DEFS.reduce(
    (acc, def) => {
      acc[def.streamType] = buildVoucherTypeMeta(def);
      return acc;
    },
    {} as Record<VoucherStreamType, VoucherTypeMeta>
  );

export const PHASE_1_STREAM_TYPES: VoucherStreamType[] = [
  'receipt',
  'expense',
  'sales_invoice',
  'purchase_invoice',
];

/** I6 P1+ posting bind batch — genesis serial chain + bank_transfer. */
export const GENESIS_POSTING_BIND_BATCH_STREAM_TYPES: VoucherStreamType[] = [
  ...PHASE_1_STREAM_TYPES,
  'bank_transfer',
];

export const PHASE_2_STREAM_TYPES: VoucherStreamType[] = [
  'bank_transfer',
  'journal',
  'reversal',
  'inventory_adjustment',
  'counting_adjustment',
  'inventory_issue',
  'inventory_shrinkage_note',
  'inventory_transformation_note',
  'inventory_transfer_note',
  'facility_charge',
];

export const PHASE_3_STREAM_TYPES: VoucherStreamType[] = [
  'purchase_order',
  'goods_receipt',
  'purchase_return',
  'sales_order',
  'delivery_note',
  'sales_return',
  'warehouse_transfer',
  'production_receipt',
  'material_issue',
  'quality_release',
];

export const PHASE_4_STREAM_TYPES: VoucherStreamType[] = [
  'payroll',
  'severance_pay',
  'asset_acquisition',
  'depreciation',
  'asset_disposal',
  'scheduled_accrual',
];

export const PHASE_5_STREAM_TYPES: VoucherStreamType[] = [
  'fx_revaluation',
  'intercompany_elimination',
  'legal_entity_opening',
];

export const PHASE_6_STREAM_TYPES: VoucherStreamType[] = [
  'service_order',
  'service_delivery',
  'consumable_receipt',
  'consumable_issue',
  'consumable_adjustment',
];

export const PHASE_7_STREAM_TYPES: VoucherStreamType[] = [
  'service_invoice',
  'service_return',
  'raw_material_receipt',
  'raw_material_adjustment',
];

export const PHASE_8_STREAM_TYPES: VoucherStreamType[] = [
  'asset_transfer',
  'asset_revaluation',
  'material_return',
  'production_scrap',
];

/** Genesis stream_definition seed targets — Phase 1–8 (E–I++ expansion). */
export const VOUCHER_GENESIS_SEED_TYPES: VoucherStreamType[] = [
  ...PHASE_1_STREAM_TYPES,
  ...PHASE_2_STREAM_TYPES,
  ...PHASE_3_STREAM_TYPES,
  ...PHASE_4_STREAM_TYPES,
  ...PHASE_5_STREAM_TYPES,
  ...PHASE_6_STREAM_TYPES,
  ...PHASE_7_STREAM_TYPES,
  ...PHASE_8_STREAM_TYPES,
];

/** UI entry/list create enabled through this phase (Stream I++: phase 8 stub enablement). */
export const VOUCHER_UI_ENABLED_MAX_PHASE = 8 as const;

/**
 * Dynamic Table row flow selection — manufacturing domains only.
 * Surfaces production-cycle vouchers (e.g. production_scrap) through manufacturing row max phase.
 */
export const MANUFACTURING_ROW_VOUCHER_UI_MAX_PHASE = 8 as const;

export function isVoucherUiEnabled(meta: VoucherTypeMeta): boolean {
  return meta.phase <= VOUCHER_UI_ENABLED_MAX_PHASE;
}

export function resolveRowVoucherUiMaxPhase(businessDomain: string): number {
  if (
    businessDomain === 'production_order' ||
    businessDomain === 'manufacturing_bom' ||
    businessDomain === 'quality_inspection'
  ) {
    return MANUFACTURING_ROW_VOUCHER_UI_MAX_PHASE;
  }
  return VOUCHER_UI_ENABLED_MAX_PHASE;
}

export function listStreamTypesUpToPhase(
  maxPhase: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
): VoucherStreamType[] {
  return listVoucherTypes().filter((type) => VOUCHER_TYPE_REGISTRY[type].phase <= maxPhase);
}

const STREAM_TYPE_ALIASES: Record<string, VoucherStreamType> = {
  sales: 'sales_invoice',
  purchase: 'purchase_invoice',
  transfer: 'bank_transfer',
  investment: 'asset_acquisition',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ENTITY_CONTEXT_SUBDOMAINS = new Set(['internal_entity', 'external_entity', 'user_entity']);

function isEntityContextSubdomain(subdomain: string): boolean {
  return ENTITY_CONTEXT_SUBDOMAINS.has(subdomain) || subdomain.endsWith('_entity');
}

/** Dynamic Table row → voucher context prefill (entity or item field by businessDomain). */
export function buildRowContextPrefill(
  fields: VoucherContextField[],
  entityId: string,
  businessDomain?: string
): Record<string, string> {
  if (businessDomain) {
    const domainMatch = fields.find((field) => field.subdomain === businessDomain);
    if (domainMatch) {
      return { [domainMatch.key]: entityId };
    }
  }

  const entityField = fields.find(
    (field) => field.required && isEntityContextSubdomain(field.subdomain)
  );
  if (entityField) {
    return { [entityField.key]: entityId };
  }

  return {};
}

export function getVoucherTypeMeta(streamType: VoucherStreamType): VoucherTypeMeta {
  return VOUCHER_TYPE_REGISTRY[streamType];
}

/** Map voucher registry entry to family-agnostic DocumentTypeMeta (I1). */
export function toDocumentTypeMeta(voucher: VoucherTypeMeta): DocumentTypeMeta {
  const flags: DocumentTypeMeta['flags'] = {};
  if (voucher.requiresBudgetCheck) flags.requiresBudgetCheck = true;
  if (voucher.requiresSourceVoucher) flags.requiresSourceVoucher = true;
  if (voucher.requiresLegalEntity) flags.requiresLegalEntity = true;
  if (voucher.requiresApproval) flags.requiresApproval = true;

  return {
    documentType: voucher.documentType,
    family: voucher.family,
    labelKo: voucher.labelKo,
    primarySubdomain: voucher.primarySubdomain,
    executionKind: voucher.executionKind,
    shellBlocks: [...voucher.shellBlocks],
    contextSchema: {
      primarySubdomain: voucher.contextSchema.primarySubdomain,
      fields: voucher.contextSchema.fields.map((field) => ({ ...field })),
      lineMode: voucher.contextSchema.lineMode,
    },
    readsSubdomains: [...voucher.readsSubdomains],
    writesSubdomains: [...voucher.writesSubdomains],
    ...(Object.keys(flags).length > 0 ? { flags } : {}),
    ...(voucher.streamTemplateId ? { streamTemplateId: voucher.streamTemplateId } : {}),
    ...(voucher.streamBind ? { streamBind: { ...voucher.streamBind } } : {}),
    category: voucher.category,
    phase: voucher.phase,
    ...(voucher.seedTemplate ? { seedTemplate: voucher.seedTemplate } : {}),
  };
}

export function listVoucherTypes(): VoucherStreamType[] {
  return Object.keys(VOUCHER_TYPE_REGISTRY) as VoucherStreamType[];
}

export function listByPhase(phase: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8): VoucherTypeMeta[] {
  return listVoucherTypes()
    .map((type) => VOUCHER_TYPE_REGISTRY[type])
    .filter((meta) => meta.phase === phase);
}

export function listByMatrixCat(cat: MatrixCat): VoucherTypeMeta[] {
  return listVoucherTypes()
    .map((type) => VOUCHER_TYPE_REGISTRY[type])
    .filter((meta) => meta.matrixCats.includes(cat));
}

export function listByCategory(category: VoucherCategory): VoucherTypeMeta[] {
  return listVoucherTypes()
    .map((type) => VOUCHER_TYPE_REGISTRY[type])
    .filter((meta) => meta.category === category);
}

export function isValidStreamType(value: string): value is VoucherStreamType {
  return value in VOUCHER_TYPE_REGISTRY;
}

export function resolveStreamTypeAlias(aliasOrType: string): VoucherStreamType | undefined {
  const normalized = aliasOrType.trim();
  if (isValidStreamType(normalized)) {
    return normalized;
  }
  return STREAM_TYPE_ALIASES[normalized];
}

/** Re-export context schema for derive/bind pipelines (I5 · I6). */
export function deriveContextSchema(meta: VoucherTypeMeta): VoucherContextSchema {
  return {
    primarySubdomain: meta.contextSchema.primarySubdomain,
    fields: meta.contextSchema.fields.map((field) => ({ ...field })),
    lineMode: meta.contextSchema.lineMode,
  };
}

function voucherRoleAllows(
  role: DocumentFamilyManifestEntry['voucherRole'],
  direction: 'read' | 'write'
): boolean {
  if (role == null) return false;
  if (role === 'both') return true;
  if (direction === 'read') {
    // write-role subdomains (finance_state, stream_execution) are valid read context
    return role === 'read' || role === 'write';
  }
  return role === 'write';
}

/**
 * Cross-check registry reads/writes against document-family-manifest voucherRole.
 * @see docs/data/document-family-manifest.json (I0)
 */
export function validateRegistryAgainstFamilyManifest(
  registry: Record<VoucherStreamType, VoucherTypeMeta> = VOUCHER_TYPE_REGISTRY,
  manifest: DocumentFamilyManifestLike
): RegistryValidationResult {
  const roleBySubdomain = new Map(
    manifest.entries.map((entry) => [entry.subdomainId, entry.voucherRole ?? null])
  );
  const violations: RegistryValidationViolation[] = [];

  for (const streamType of Object.keys(registry) as VoucherStreamType[]) {
    const meta = registry[streamType];

    for (const subdomainId of meta.readsSubdomains) {
      const role = roleBySubdomain.get(subdomainId);
      if (!voucherRoleAllows(role, 'read')) {
        violations.push({
          streamType,
          subdomainId,
          direction: 'read',
          message: `readsSubdomains includes "${subdomainId}" but manifest voucherRole=${String(role)}`,
        });
      }
    }

    for (const subdomainId of meta.writesSubdomains) {
      const role = roleBySubdomain.get(subdomainId);
      if (!voucherRoleAllows(role, 'write')) {
        violations.push({
          streamType,
          subdomainId,
          direction: 'write',
          message: `writesSubdomains includes "${subdomainId}" but manifest voucherRole=${String(role)}`,
        });
      }
    }
  }

  return { ok: violations.length === 0, violations };
}

/** Skeleton — full matrix-relations reachability checks land in I5. */
export function validateRegistryAgainstMatrix(
  registry: Record<VoucherStreamType, VoucherTypeMeta> = VOUCHER_TYPE_REGISTRY
): RegistryValidationResult {
  const violations: RegistryValidationViolation[] = [];

  for (const streamType of Object.keys(registry) as VoucherStreamType[]) {
    const meta = registry[streamType];
    if (meta.writesSubdomains.length === 0) {
      violations.push({
        streamType,
        subdomainId: '*',
        direction: 'write',
        message: 'writesSubdomains must not be empty',
      });
    }
    if (!meta.contextSchema.lineMode) {
      violations.push({
        streamType,
        subdomainId: '*',
        direction: 'read',
        message: 'contextSchema.lineMode is required',
      });
    }
  }

  return { ok: violations.length === 0, violations };
}
